import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

// «لوحة أدائي» — the student's own performance picture. Registered students
// only; it refreshes itself every time a session's results are saved.
const C = {
  primary: "#0091F7",
  green: "#2e7d32",
  amber: "#f6a609",
  red: "#d32f2f",
  ink: "#17202a",
  muted: "#6b7a89",
  soft: "#F4F7FB",
};

const StatCard = ({ icon, value, label, tint, sub }) => (
  <Box
    sx={{
      flex: "1 1 150px",
      minWidth: 150,
      background: "#fff",
      borderRadius: "18px",
      padding: "16px 18px",
      boxShadow: "0 2px 14px rgba(16,32,48,0.07)",
      borderTop: `4px solid ${tint}`,
    }}
  >
    <Typography fontSize="1.5rem" lineHeight={1}>{icon}</Typography>
    <Typography fontSize="1.9rem" fontWeight={800} color={C.ink} sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.25 }}>
      {value}
    </Typography>
    <Typography fontSize="0.85rem" color={C.muted} fontWeight={600}>{label}</Typography>
    {sub && <Typography fontSize="0.72rem" color={C.muted}>{sub}</Typography>}
  </Box>
);

// accuracy ring drawn with a plain SVG — no chart library needed
const AccuracyRing = ({ value }) => {
  const size = 132;
  const stroke = 13;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value ?? 0));
  const color = pct >= 80 ? C.green : pct >= 50 ? C.amber : C.red;

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e8eef4" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (pct / 100) * circumference}
          style={{ transition: "stroke-dashoffset 900ms ease" }}
        />
      </svg>
      <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <Typography fontSize="1.8rem" fontWeight={800} color={color} sx={{ lineHeight: 1 }}>{pct}%</Typography>
        <Typography fontSize="0.7rem" color={C.muted} fontWeight={700}>الدقة</Typography>
      </Box>
    </Box>
  );
};

// progress over the last sessions — bars, drawn with divs
const ProgressChart = ({ points }) => {
  if (!points || points.length < 2) return null;
  const max = Math.max(...points.map((p) => p.score), 1);

  return (
    <Box>
      <Typography fontWeight={800} color={C.ink} mb={0.5}>📈 تطوّري عبر الحصص</Typography>
      <Typography fontSize="0.78rem" color={C.muted} mb={2}>نقاطي في آخر {points.length} حصص</Typography>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: "8px", height: 150, direction: "ltr" }}>
        {points.map((p, i) => {
          const height = Math.max(6, (p.score / max) * 130);
          const isLast = i === points.length - 1;
          return (
            <Tooltip key={i} title={`${p.title} — ${p.score} نقطة${p.accuracy !== null ? ` · دقة ${p.accuracy}%` : ""}`}>
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "5px", cursor: "default" }}>
                <Typography fontSize="0.68rem" fontWeight={700} color={isLast ? C.primary : C.muted}>
                  {p.score}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 42,
                    height: `${height}px`,
                    borderRadius: "8px 8px 4px 4px",
                    background: isLast
                      ? `linear-gradient(180deg, ${C.primary}, #00c2ff)`
                      : "linear-gradient(180deg, #b9d7ef, #d8e7f3)",
                    transition: "height 700ms ease",
                  }}
                />
                <Typography fontSize="0.6rem" color={C.muted} sx={{ whiteSpace: "nowrap" }}>
                  {(p.date || "").slice(5)}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

const Segment = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Box mb={1.2}>
      <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
        <Typography fontSize="0.82rem" fontWeight={700} color={C.ink}>{label}</Typography>
        <Typography fontSize="0.82rem" fontWeight={700} color={color} sx={{ fontVariantNumeric: "tabular-nums" }}>
          {value} ({pct}%)
        </Typography>
      </Box>
      <Box sx={{ height: 9, borderRadius: "6px", background: "#e8eef4", overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${pct}%`, background: color, borderRadius: "6px", transition: "width 800ms ease" }} />
      </Box>
    </Box>
  );
};

const TREND = {
  up: { icon: "📈", text: "أداؤك في تحسّن مستمر، أحسنت!", color: C.green, bg: "#e8f5e9" },
  down: { icon: "📉", text: "أداؤك تراجع قليلاً — راجع دروسك وستعود أقوى.", color: C.red, bg: "#ffebee" },
  steady: { icon: "➖", text: "أداؤك ثابت — حاول رفع مستواك في الحصة القادمة.", color: C.amber, bg: "#fff8e1" },
};

const PerformanceDashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/whiteboard/my-dashboard`)
      .then((response) => setData(response?.data?.data))
      .catch(() => setError(true));
  }, []);

  if (error) return null;

  if (!data) {
    return (
      <Paper elevation={2} sx={{ borderRadius: "20px", padding: "40px", width: "min(700px, 100%)", marginTop: "20px", textAlign: "center" }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  const { totals, chart, sessions, badges, best_session: best, trend } = data;

  if (totals.sessions === 0) {
    return (
      <Paper elevation={2} sx={{ borderRadius: "20px", padding: "30px", width: "min(700px, 100%)", marginTop: "20px", textAlign: "center" }}>
        <Typography fontSize="2.4rem">📊</Typography>
        <Typography variant="h6" fontWeight={800} color={C.ink}>لوحة أدائي</Typography>
        <Typography color={C.muted} fontSize="0.9rem" mt={1}>
          لم تشارك في أي حصة تفاعلية بعد — ستظهر هنا نقاطك ودقّتك وتطوّرك فور انتهاء أول حصة.
        </Typography>

        {/* badges can exist before any saved session (the teacher may award
            one by hand) — they must still be visible */}
        <Box sx={{ marginTop: 3 }}>
          <Typography fontWeight={800} color={C.ink} mb={1.5}>
            🏅 شاراتي {badges.length > 0 && `(${badges.length})`}
          </Typography>
          {badges.length === 0 ? (
            <Typography fontSize="0.85rem" color={C.muted}>
              لم تحصل على شارات بعد — شارك في الحصص وأجب بشكل صحيح لتكسب أول شارة.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "14px", justifyContent: "center" }}>
              {badges.map((badge) => (
                <Tooltip key={badge.id} title={`${badge.description || badge.name} — ${badge.date}`}>
                  <Box sx={{ width: 88, textAlign: "center" }}>
                    <Box sx={{
                      width: 56, height: 56, margin: "0 auto 6px", borderRadius: "50%",
                      background: badge.color, display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "1.8rem", boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                    }}>
                      {badge.icon}
                    </Box>
                    <Typography fontSize="0.74rem" fontWeight={700} lineHeight={1.3}>{badge.name}</Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          )}
        </Box>
      </Paper>
    );
  }

  const tone = trend ? TREND[trend] : null;

  return (
    <Paper
      elevation={2}
      sx={{ borderRadius: "20px", padding: "26px", width: "min(700px, 100%)", marginTop: "20px", background: C.soft }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, marginBottom: 0.5, flexWrap: "wrap" }}>
        <Typography variant="h6" fontWeight={800} color={C.ink}>📊 لوحة أدائي</Typography>
        <Typography fontSize="0.78rem" color={C.muted}>تتحدّث تلقائياً بعد كل حصة</Typography>
      </Box>

      {/* headline numbers */}
      <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: 2 }}>
        <StatCard icon="🎯" value={totals.points} label="مجموع نقاطي" tint={C.primary} sub={`بمعدل ${totals.average_score} لكل حصة`} />
        <StatCard icon="📚" value={totals.sessions} label="حصة شاركت فيها" tint="#8e24aa" />
        <StatCard icon="⭐" value={totals.stars} label="نجمة" tint={C.amber} />
        <StatCard icon="🏅" value={totals.badges} label="شارة" tint={C.green} />
      </Box>

      {tone && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, background: tone.bg, color: tone.color, borderRadius: "14px", padding: "12px 16px", marginTop: 2, fontWeight: 700 }}>
          <Typography fontSize="1.3rem">{tone.icon}</Typography>
          <Typography fontSize="0.92rem" fontWeight={700}>{tone.text}</Typography>
        </Box>
      )}

      {/* accuracy + answer split */}
      <Box sx={{ background: "#fff", borderRadius: "18px", padding: "20px", marginTop: 2, boxShadow: "0 2px 14px rgba(16,32,48,0.07)" }}>
        <Typography fontWeight={800} color={C.ink} mb={2}>🎯 دقّة إجاباتي</Typography>
        <Box sx={{ display: "flex", gap: "24px", alignItems: "center", flexWrap: "wrap" }}>
          <AccuracyRing value={totals.accuracy} />
          <Box sx={{ flex: "1 1 260px", minWidth: 240 }}>
            <Segment label="✅ إجابات كاملة الدرجة" value={totals.full} total={totals.answered} color={C.green} />
            <Segment label="🟡 إجابات جزئية" value={totals.partial} total={totals.answered} color={C.amber} />
            <Segment label="❌ إجابات خاطئة" value={totals.zero} total={totals.answered} color={C.red} />
            <Typography fontSize="0.78rem" color={C.muted} mt={1}>
              أجبت على {totals.answered} سؤالاً في {totals.sessions} حصة
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* progress */}
      {chart.length >= 2 && (
        <Box sx={{ background: "#fff", borderRadius: "18px", padding: "20px", marginTop: 2, boxShadow: "0 2px 14px rgba(16,32,48,0.07)" }}>
          <ProgressChart points={chart} />
        </Box>
      )}

      {/* best session */}
      {best && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, background: "linear-gradient(135deg, #fff8e1, #fffdf5)", border: "1px solid #f6c945", borderRadius: "16px", padding: "14px 18px", marginTop: 2 }}>
          <Typography fontSize="1.8rem">🏆</Typography>
          <Box>
            <Typography fontSize="0.75rem" color={C.muted} fontWeight={700}>أفضل حصة لي</Typography>
            <Typography fontWeight={800} color={C.ink}>{best.title}</Typography>
            <Typography fontSize="0.8rem" color={C.muted}>
              {best.total_score} نقطة{best.accuracy !== null ? ` · دقة ${best.accuracy}%` : ""} · {best.date}
            </Typography>
          </Box>
        </Box>
      )}

      {/* badges — always shown, even before the first one is earned */}
      <Box sx={{ background: "#fff", borderRadius: "18px", padding: "20px", marginTop: 2, boxShadow: "0 2px 14px rgba(16,32,48,0.07)" }}>
        <Typography fontWeight={800} color={C.ink} mb={1.5}>
          🏅 شاراتي {badges.length > 0 && `(${badges.length})`}
        </Typography>
        {badges.length === 0 ? (
          <Typography fontSize="0.85rem" color={C.muted}>
            لم تحصل على شارات بعد — شارك في الحصص وأجب بشكل صحيح لتكسب أول شارة.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "14px" }}>
            {badges.map((badge) => (
              <Tooltip key={badge.id} title={`${badge.description || badge.name} — ${badge.date}`}>
                <Box sx={{ width: 88, textAlign: "center" }}>
                  <Box sx={{
                    width: 56, height: 56, margin: "0 auto 6px", borderRadius: "50%",
                    background: badge.color, display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "1.8rem", boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                  }}>
                    {badge.icon}
                  </Box>
                  <Typography fontSize="0.74rem" fontWeight={700} lineHeight={1.3}>{badge.name}</Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
        )}
      </Box>

      {/* activity log */}
      <Box sx={{ background: "#fff", borderRadius: "18px", padding: "20px", marginTop: 2, boxShadow: "0 2px 14px rgba(16,32,48,0.07)" }}>
        <Typography fontWeight={800} color={C.ink} mb={1.5}>📋 سجل حصصي</Typography>
        {sessions.map((session) => (
          <Accordion key={session.id} disableGutters elevation={0} sx={{ background: C.soft, borderRadius: "12px !important", marginBottom: "8px", "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", flexWrap: "wrap" }}>
                <Typography fontWeight={700} sx={{ flex: 1, minWidth: 120 }}>{session.title}</Typography>
                <Typography fontSize="0.75rem" color={C.muted}>{session.date}</Typography>
                {session.accuracy !== null && (
                  <Chip size="small" label={`${session.accuracy}%`}
                    sx={{
                      fontWeight: 800,
                      background: session.accuracy >= 80 ? "#e8f5e9" : session.accuracy >= 50 ? "#fff8e1" : "#ffebee",
                      color: session.accuracy >= 80 ? C.green : session.accuracy >= 50 ? "#7a4c06" : C.red,
                    }} />
                )}
                <Chip size="small" label={`${session.total_score} نقطة`} sx={{ fontWeight: 800, background: "#e3f1fc", color: "#0b4f7a" }} />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {(session.details || []).map((detail, index) => (
                <Box key={index} sx={{
                  display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap",
                  padding: "8px 12px", borderRadius: "10px", background: "#fff", marginBottom: "6px",
                }}>
                  {detail.kind === "question" ? (
                    <>
                      <Typography fontSize="0.88rem" sx={{ flex: 1, minWidth: 150 }}>
                        {detail.score >= detail.max_score ? "✅" : detail.score > 0 ? "🟡" : "❌"} {detail.title}
                      </Typography>
                      <Typography fontSize="0.88rem" fontWeight={800} color={detail.score >= detail.max_score ? C.green : detail.score > 0 ? C.amber : C.red}>
                        {detail.score} / {detail.max_score}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography fontSize="0.88rem" sx={{ flex: 1 }}>
                        {detail.kind === "activities" ? "🏅 نقاط الأنشطة" : "⭐ نقاط النجوم"} × {detail.count}
                      </Typography>
                      <Typography fontSize="0.88rem" fontWeight={800} color={C.primary}>{detail.points}</Typography>
                    </>
                  )}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Paper>
  );
};

export default PerformanceDashboard;
